import React from 'react';
import { Box, Paper, Typography, Chip, Stack, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorIcon from '@mui/icons-material/Error';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import HistoryIcon from '@mui/icons-material/History';
import { Referral, ReferralStatus, ReferralStage, ReferralSource, FlagSeverity } from '~/types/transition';
import { useTransitionContext } from '~/hooks/transition/useTransitionContext';
import { useTransitionAttention } from '~/providers/TransitionAttentionProvider';
import SourceBadge from '../common/SourceBadge';

function timeAgo(isoString: string): string {
  const ms = Date.now() - new Date(isoString).getTime();
  const minutes = Math.floor(ms / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

const STATUS_LABELS: Record<ReferralStatus, string> = {
  [ReferralStatus.RECEIVED]: 'Received',
  [ReferralStatus.PROCESSING]: 'Processing...',
  [ReferralStatus.PROCESSING_COMPLETE]: 'Processing complete',
  [ReferralStatus.AWAITING_INFO]: 'Awaiting info',
  [ReferralStatus.RECOMMENDATION_READY]: 'Ready for decision',
  [ReferralStatus.PENDING_REVIEW]: 'Pending review',
  [ReferralStatus.ACCEPTED]: 'Accepted',
  [ReferralStatus.DECLINED]: 'Declined',
  [ReferralStatus.AUTO_DECLINED]: 'Auto-Declined',
  [ReferralStatus.BLOCKED]: 'Blocked',
};

interface ReferralCardProps {
  referral: Referral;
}

const ReferralCard: React.FC<ReferralCardProps> = ({ referral }) => {
  const navigate = useNavigate();
  const { beginProcessing } = useTransitionContext();
  const { isUnseen } = useTransitionAttention();
  const showNewIndicator = isUnseen(referral.id);

  const needsManualStart =
    referral.stage === ReferralStage.INTAKE &&
    (referral.source === ReferralSource.FAX || referral.source === ReferralSource.PHONE);

  const blockingFlags = referral.flags.filter((f) => f.severity === FlagSeverity.BLOCKING);
  const warningFlags = referral.flags.filter((f) => f.severity === FlagSeverity.WARNING);
  const hasBlocker = blockingFlags.length > 0;
  const hasWarning = warningFlags.length > 0;

  return (
    <Paper
      onClick={() => navigate(`/transition/referral/${referral.id}`)}
      variant="outlined"
      sx={{
        p: 1.5,
        cursor: 'pointer',
        position: 'relative',
        borderLeftWidth: hasBlocker || hasWarning ? 3 : 1,
        borderLeftColor: hasBlocker ? 'error.main' : hasWarning ? 'warning.main' : 'divider',
        '&:hover': {
          backgroundColor: 'action.hover',
          borderColor: hasBlocker ? 'error.main' : hasWarning ? 'warning.main' : 'primary.main',
          borderLeftColor: hasBlocker ? 'error.main' : hasWarning ? 'warning.main' : 'primary.main',
        },
        transition: 'background-color 0.15s',
      }}
    >
      {showNewIndicator && (
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: 'primary.main',
          }}
        />
      )}
      <Typography variant="body2" fontWeight={600} noWrap>
        {referral.patient.name}
      </Typography>

      <Box sx={{ mt: 0.75, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        <SourceBadge source={referral.source} size="small" />
        {(referral.priorEncounters?.length ?? 0) > 0 ? (
          <Chip
            icon={<HistoryIcon sx={{ fontSize: '0.7rem !important' }} />}
            label="Returning"
            size="small"
            variant="outlined"
            sx={{ fontSize: '0.72rem', height: 22, fontWeight: 600, color: 'text.secondary', borderColor: 'divider' }}
          />
        ) : (
          <Chip
            label="New"
            size="small"
            variant="outlined"
            sx={{ fontSize: '0.72rem', height: 22, color: 'text.disabled', borderColor: 'divider' }}
          />
        )}
        {hasBlocker && (
          <Chip
            icon={<ErrorIcon sx={{ fontSize: '0.8rem !important' }} />}
            label={`${blockingFlags.length} blocker`}
            size="small"
            color="error"
            sx={{ fontSize: '0.65rem', height: 20, fontWeight: 600 }}
          />
        )}
        {!hasBlocker && hasWarning && (
          <Chip
            icon={<WarningAmberIcon sx={{ fontSize: '0.8rem !important' }} />}
            label={`${warningFlags.length} warning`}
            size="small"
            color="warning"
            sx={{ fontSize: '0.65rem', height: 20, fontWeight: 600 }}
          />
        )}
      </Box>

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 0.75 }}>
        <Typography variant="caption" color="text.secondary">
          {STATUS_LABELS[referral.status]}
        </Typography>
        <Typography variant="caption" color="text.disabled">
          {timeAgo(referral.receivedAt)}
        </Typography>
      </Stack>

      {needsManualStart && (
        <Button
          size="small"
          variant="contained"
          startIcon={<PlayArrowIcon sx={{ fontSize: '0.8rem !important' }} />}
          onClick={(e) => {
            e.stopPropagation();
            beginProcessing(referral.id);
          }}
          sx={{ mt: 1, width: '100%', fontSize: '0.7rem', py: 0.25 }}
        >
          Begin Processing
        </Button>
      )}
    </Paper>
  );
};

export default ReferralCard;
